import { CloudDownload } from '@mui/icons-material';
import { IconButton, SimplePaletteColorOptions, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';

import { useClientContext } from 'context/ClientContext';
import { useImage } from 'hooks/useImage';
import { useQueryComunicado } from 'pages/comunicado/hooks';
import { RequestComunicadosModelProps } from 'pages/comunicado/types';
import { downloadHtmlAsPdf } from 'util/downloadHtmlAsPdf';

type ComunicadoDownloadBtn = {
    idComunicado?: number;
    comunicado?: RequestComunicadosModelProps;
};

/**
 * Retorna o HTML da categoria do comunicado formatado para download.
 * Se não houver categoria, retorna uma string vazia.
 */
const getHtmlCategoriaComunicado = (comunicado: RequestComunicadosModelProps, secondaryColor: string) => {
    if (!comunicado.categoriaComunicado?.dsCategoriaComunicado) {
        return '';
    }

    return `<div style="font-size: 16px">
        <span style="color: ${secondaryColor}">Categoria:</span>
        <span>${comunicado.categoriaComunicado?.dsCategoriaComunicado}</span>
    </div>`;
};

/**
 * Retorna o HTML da data de publicação do comunicado formatado para download.
 * Se não houver data de publicação, retorna uma string vazia.
 */
const getHtmlPublicacaoComunicado = (comunicado: RequestComunicadosModelProps, secondaryColor: string) => {
    if (!comunicado.dhInclusao) {
        return '';
    }

    return `<div style="font-size: 16px">
        <span style="color: ${secondaryColor}">Publicado:</span>
        <span>${format(new Date(comunicado.dhInclusao as string), 'dd/MM/yyyy')}</span>
    </div>`;
};

/**
 * Retorna o HTML das tags do comunicado formatado para download.
 * Se não houver tags, retorna uma string vazia.
 */
const getHtmlTagsComunicado = (comunicado: RequestComunicadosModelProps, secondaryColor: string) => {
    if (!comunicado.comunicadoTagsComunicado?.length) {
        return '';
    }

    return `<div style="font-size: 16px">
        <span style="color: ${secondaryColor}">Hashtags:</span>
        <span>${comunicado.comunicadoTagsComunicado?.map((item) => item.dsTagComunicado).join(', ')}</span>
    </div>`;
};

/**
 * Retorna o HTML do comunicado formatado para download.
 */
const getHtmlComunicado = (comunicado: RequestComunicadosModelProps, secondaryColor: string, image: any) => {
    return `<div style="background-color: ${secondaryColor}; padding:12px; height:64px; width:100%">
            <img style="width: auto; height: 100%" src=${image} />
        </div>

        <div style="display: flex; flex-direction: column; padding: 20px">
            <div style="color: ${secondaryColor}; font-weight: 200; font-size:28px; margin-bottom: 8px">
                ${comunicado.dsComunicado}
            </div>

            ${getHtmlCategoriaComunicado(comunicado, secondaryColor)}

            ${getHtmlPublicacaoComunicado(comunicado, secondaryColor)}
            
            ${getHtmlTagsComunicado(comunicado, secondaryColor)}
        </div>

        <div style="margin: 0px 20px; opacity: 50%; border-bottom: 1px solid ${secondaryColor}"></div>

        <div style="padding: 8px 20px 20px 20px">${comunicado.dsConteudo}</div>`;
};

export function ComunicadoDownloadBtn({ idComunicado, comunicado }: ComunicadoDownloadBtn) {
    const { refetch, isFetching } = useQueryComunicado({ id: idComunicado });
    const { logo, theme } = useClientContext();
    const { image } = useImage(logo);
    const secondaryColor = (theme.palette?.secondary as SimplePaletteColorOptions).main;

    /**
     * Retorna uma promise com os valores do comunicado.
     * Se o conteúdo já estiver disponível, retorna ele diretamente.
     */
    const getComunicado = () => {
        if (comunicado) {
            return Promise.resolve({ data: comunicado });
        } else if (idComunicado) {
            return refetch();
        }

        return Promise.reject('Falha ao buscar o comunicado para download');
    };

    /**
     * Realiza o download do comunicado e abre o pdf.
     */
    const downloadComunicado = () =>
        getComunicado()
            .then((res) => {
                const html = getHtmlComunicado(res.data as RequestComunicadosModelProps, secondaryColor, image);

                downloadHtmlAsPdf(html, res.data?.dsComunicado);
            })
            .catch((e) => enqueueSnackbar(e, { variant: 'error' }));

    return (
        <Tooltip title="Download do comunicado">
            <IconButton sx={{ p: 0.5 }} onClick={downloadComunicado} disabled={isFetching}>
                <CloudDownload />
            </IconButton>
        </Tooltip>
    );
}
